import { createMutation } from "@/api/utils";
import { QueryStringBuilder } from "@/utils";
import {
    CreditBoxesResultsPayload,
    CreditBoxesResultsResponse,
    CreditBoxRunPayload,
    CreditBoxTask,
} from "./CreditBox.types";

export const getCreditBoxesResults = createMutation<
    CreditBoxesResultsResponse,
    CreditBoxesResultsPayload
>(
    ({ api, payload }) => {
        const queryBuilder = new QueryStringBuilder();
        const paramMapping = {
            creditBoxId: "credit_box_id",
            opportunityName: "opportunity_name",
            icmId: "icm_id",
            icmUuid: "icm_uuid",
            sfdcOpportunityId: "sfdc_opportunity_id",
            score: "score",
        };

        Object.entries(paramMapping).forEach(([payloadKey, queryParamName]) => {
            const value =
                payload?.[payloadKey as keyof CreditBoxesResultsPayload];
            if (value) {
                queryBuilder.in(
                    queryParamName,
                    Array.isArray(value) ? value : [value],
                );
            }
        });

        const queryString = queryBuilder.build();

        return api.get(
            `/deal-flow/v1/credit_boxes/results/spreadsheet?${queryString}`,
        );
    },
    {
        errorToast: {
            message: "Unable to generate credit boxes results.",
        },
    },
);

export const getCreditBoxesResultsTask = createMutation<
    CreditBoxTask,
    CreditBoxesResultsPayload
>(({ api, payload }) => {
    return api.post(`/deal-flow/v2/credit_boxes/results/spreadsheet`, payload);
});

export const createCreditBoxFromSfdc = createMutation<CreditBoxTask>(
    ({ api }) => {
        return api.post("/deal-flow/v1/credit_boxes/generate_from_sfdc");
    },
    {
        invalidates: ["/credit_boxes/investor_map"],
    },
);

export const updateOpportunitiesFromSfdc = createMutation<CreditBoxTask>(
    ({ api }) => {
        return api.post("/deal-flow/v1/opportunities/update_from_sfdc");
    },
    {
        invalidates: ["/credit_boxes/opportunity_map"],
    },
);

export const runCreditBoxes = createMutation<
    CreditBoxTask,
    CreditBoxRunPayload
>(({ api, payload }) => {
    return api.post("/deal-flow/v1/credit_boxes/run", {
        creditBoxUuids: payload?.creditBoxUuids || [],
        icmCompanies: payload?.icmCompanies || [],
        sfdcOpportunities: payload?.sfdcOpportunities || [],
        prospectiveValues: payload?.prospectiveValues || [],
        testCreditBox: payload?.testCreditBox || {},
    });
});
